import { useState, useMemo } from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  DialogContentText,
  IconButton,
  TextField,
  Typography,
  DialogTitle,
  Grid,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tooltip,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import SaveIcon from '@mui/icons-material/Save';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { updateFormValues } from '../../../redux/slices/reports';
import SimpleAutocompleteProps from '../../../reusable-components/autocomplete/SimpleAutocompleteProps';
import { DateRangePickerSelect, MultiDateRange } from '../../../reusable-components/date-range-picker';
import SpacedRow from '../../../reusable-components/spaced-row/SpacedRow';
import { fDate } from '../../../utils/formatTime';

ReportSelectSpecificationsStep.propTypes = {
  setShowDatePicker: PropTypes.func,
  showDatePicker: PropTypes.bool,
  toggleStep: PropTypes.string,
  isScheduleReport: PropTypes.bool,
};

export default function ReportSelectSpecificationsStep({
  showDatePicker,
  setShowDatePicker,
  toggleStep,
  isScheduleReport,
}) {
  const MAX_CUSTOM_RANGE_DAYS = 90;
  const dispatch = useDispatch();
  const { formValues, createReportData, reportSpecificationOptions, selectedReportId, downloadingReportResults } =
    useSelector((state) => state.reports.data);

  const facilitiesAndCompanies = useMemo(() => {
    const facilities = createReportData.facilities || [];
    const companies = createReportData.companies || [];
    if (
      !formValues?.selectedReport?.typeId ||
      !createReportData.reportPermissionsByType ||
      !createReportData.reportPermissionsByType[formValues.selectedReport.typeId]
    )
      return { facilities, companies };
    const filteredFacilies = facilities.filter((facility) =>
      createReportData.reportPermissionsByType[formValues.selectedReport.typeId].includes(facility.value)
    );
    const companyIds = filteredFacilies.map((facility) => facility.companyId);
    if (
      formValues?.facilities?.some(
        (facility) =>
          !createReportData.reportPermissionsByType[formValues.selectedReport.typeId].includes(facility.value)
      ) ||
      formValues?.companies?.some((company) => !companyIds.includes(company.value))
    ) {
      dispatch(
        updateFormValues({
          ...formValues,
          facilities: formValues.facilities?.filter((facility) =>
            createReportData.reportPermissionsByType[formValues.selectedReport.typeId].includes(facility.value)
          ),
          companies: formValues.companies?.filter((company) => companyIds.includes(company.value)),
        })
      );
    }
    return {
      facilities: filteredFacilies,
      companies: companies.filter((company) => companyIds.includes(company.value)),
    };
  }, [createReportData, formValues?.selectedReport]);

  const [showAllVendors, setShowAllVendors] = useState(false);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [showAllSubCategories, setShowAllSubCategories] = useState(false);
  const [showAllTimeFrameFilters, setShowAllTimeFrameFilters] = useState(false);
  const [showAllFacilities, setShowAllFacilities] = useState(false);
  const [showAllGroups, setShowAllGroups] = useState(false);
  const [showAllRentalVendors, setShowAllRentalVendors] = useState(false);
  const [showAllTransportStatuses, setShowAllTransportStatuses] = useState(false);
  const [filteredReportFacilities, setFilteredReportFacilities] = useState(facilitiesAndCompanies.facilities || []);
  const [filteredReportSubcats, setFilteredReportSubcats] = useState(createReportData.subcategories || []);
  const [customDateRangeError, setCustomDateRangeError] = useState('');
  const user = useSelector((state) => state.user.user);

  const timeframeFilters = createReportData?.timeFrameFilters?.map((f) => ({ value: f.id, label: f.name }));
  const handleCheckboxChange = (name, checked) => {
    dispatch(updateFormValues({ ...formValues, [name]: checked }));
  };
  const renderTags = (value, getTagProps, showAll, setShowAll) => {
    const displayedValues = showAll ? value : value.slice(0, 3);
    return (
      <>
        {displayedValues.map((option, index) => (
          <Chip key={option.value} label={option.label} {...getTagProps({ index })} style={{ marginRight: 4 }} />
        ))}
        {value.length > 3 && (
          <IconButton
            onClick={() => {
              setShowAll(!showAll);
            }}
            size="small"
            style={{ marginLeft: 4 }}
          >
            {showAll ? (
              <ExpandLessIcon />
            ) : (
              <>
                {' '}
                + {value.length - 3}
                <ExpandMoreIcon />
              </>
            )}
          </IconButton>
        )}
      </>
    );
  };
  const visibleSpecificationOptions = useMemo(
    () => reportSpecificationOptions.filter((o) => !(o.isGenerateOnly && isScheduleReport)),
    [reportSpecificationOptions, isScheduleReport]
  );

  const skipOption = visibleSpecificationOptions.find((o) => o.name === 'Skip');
  const takeOption = visibleSpecificationOptions.find((o) => o.name === 'Take');
  const dateOption = visibleSpecificationOptions.find((o) => o.name === 'DateFilter');
  const companiesOption = visibleSpecificationOptions.find((o) => o.name === 'Companies');
  const facilityOption = visibleSpecificationOptions.find((o) => o.name === 'Facilities');
  const vendorOption = visibleSpecificationOptions.find((o) => o.name === 'Vendors');
  const categoriesOption = visibleSpecificationOptions.find((o) => o.name === 'Categories');
  const subcategoriesOption = visibleSpecificationOptions.find((o) => o.name === 'Subcategories');
  const timeFrameFilterOptions = visibleSpecificationOptions.find((o) => o.name === 'TimeFrameFilters');
  const monthOptions = visibleSpecificationOptions.find((o) => o.name === 'Month');
  const yearOptions = visibleSpecificationOptions.find((o) => o.name === 'Year');
  const dateBetween = visibleSpecificationOptions.find((o) => o.name === 'DateBetween');
  const excTicketData = visibleSpecificationOptions.find((o) => o.name === 'ExcludeTicketData');
  const incFacsWOGrps = visibleSpecificationOptions.find((o) => o.name === 'IncludeFacsWithoutGroup');
  const incChildFacs = visibleSpecificationOptions.find((o) => o.name === 'IncludeChildFacs');
  const serialNumbers = visibleSpecificationOptions.find((o) => o.name === 'SerialNumbers');
  const rentalVendor = visibleSpecificationOptions.find((o) => o.name === 'RentalVendor');
  const pendingOnly = visibleSpecificationOptions.find((o) => o.name === 'PendingOnly');
  const priceChangesOnly = reportSpecificationOptions.find((o) => o.name === 'PriceChangesOnly');
  const useBudgetDate = reportSpecificationOptions.find((o) => o.name === 'UseBudgetDate');
  const customDateRange = visibleSpecificationOptions.find((o) => o.name === 'CustomDateRange');
  const transportStatuses = visibleSpecificationOptions.find((o) => o.name === 'TransportStatuses');

  const numberInputDefaults = { type: 'number', size: 'small' };

  const autoCompleteDefaults = (opt, options, values, showAllTags, setShowAllTags, onChange) => ({
    ...SimpleAutocompleteProps,
    multiple: opt.multiselect,
    disableCloseOnSelect: opt.multiselect,
    sx: { mt: 1 },
    options: opt.multiselect ? [{ label: 'Select All', value: 'All' }, ...(options || [])] : options || [],
    autoHighlight: true,
    value: opt.multiselect ? values || [] : values && values.length > 0 ? values[0] : null,
    getOptionKey: (opt) => opt?.value,
    onChange: (event, newValue) => {
      const newValueArray = newValue ? (!opt.multiselect ? [newValue] : newValue) : [];
      onChange(
        newValueArray.some((option) => option.value === 'All')
          ? newValueArray.length - 1 === options?.length
            ? []
            : options
          : newValueArray
      );
    },
    renderTags: (value, getTagProps) => renderTags(value, getTagProps, showAllTags, setShowAllTags),
  });
  const [inputValue, setInputValue] = useState('');
  const handleSave = (rawValue) => {
    const valueToUse = rawValue ?? inputValue;
    if (!valueToUse.trim()) return;
    const parsed = parseInput(valueToUse);
    const newValues = [...(formValues.serialNumbers || []), ...parsed];
    dispatch(
      updateFormValues({
        ...formValues,
        serialNumbers: [...new Set(newValues)],
      })
    );

    setInputValue('');
  };

  const parseInput = (input) =>
    input
      .split(/[\s,]+/)
      .map((v) => v.trim())
      .filter((v) => v !== '');

  return (
    <>
      {dateOption && !dateOption.multiselect && (
        <>
          <DialogContentText sx={{ fontWeight: 'bold', mb: -1, mt: 0 }}>Date:</DialogContentText>
          <DateRangePickerSelect
            noStickyFilter
            buttonSx={{ paddingTop: '15px !important', paddingBottom: '15px !important', marginTop: '15px !important' }}
            datePickerSx={{ paddingTop: '5px !important', paddingBottom: '5px !important' }}
            value={[formValues.startDate, formValues.endDate]}
            setValue={(start, end) => dispatch(updateFormValues({ ...formValues, startDate: start, endDate: end }))}
          />
        </>
      )}
      {customDateRange && (
        <>
          <DialogContentText sx={{ fontWeight: 'bold' }}>Date Range:</DialogContentText>
          <DateRangePicker
            value={[formValues.startDate || null, formValues.endDate || null]}
            onChange={(newValue) => {
              const [startDate, endDate] = newValue || [];

              if (startDate && endDate) {
                const startUtc = Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
                const endUtc = Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
                const diffInDays = Math.abs((endUtc - startUtc) / (1000 * 60 * 60 * 24));

                if (diffInDays > MAX_CUSTOM_RANGE_DAYS) {
                  setCustomDateRangeError(`Date range cannot be more than ${MAX_CUSTOM_RANGE_DAYS} days.`);
                  return;
                }
              }

              setCustomDateRangeError('');
              dispatch(
                updateFormValues({
                  ...formValues,
                  startDate: startDate || null,
                  endDate: endDate || null,
                })
              );
            }}
            slotProps={{
              textField: {
                size: 'small',
              },
            }}
            sx={{
              mt: 1,
              width: '100%',
              '& .MuiInputBase-input.MuiOutlinedInput-input': {
                p: '8px 10px',
              },
            }}
          />
          {customDateRangeError && (
            <Typography color="error" variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              {customDateRangeError}
            </Typography>
          )}
        </>
      )}
      {dateOption?.multiselect && (
        <MultiDateRange
          datesValue={formValues?.dateValues}
          setDatesValue={(dateValues) => dispatch(updateFormValues({ ...formValues, dateValues }))}
          showFullDatePicker={showDatePicker}
          setShowFullDatePicker={setShowDatePicker}
        />
      )}

      {dateBetween && (
        <>
          <DialogContentText sx={{ fontWeight: 'bold' }}>Take from between these dates:</DialogContentText>
          <Typography color="#637381" mb={-1}>
            {formValues?.startBetweenDate
              ? `${fDate(formValues?.startBetweenDate)} -  ${fDate(formValues?.endBetweenDate)}`
              : ''}
          </Typography>
          <DateRangePickerSelect
            noStickyFilter
            buttonSx={{ paddingTop: '15px !important', paddingBottom: '15px !important', marginTop: '15px !important' }}
            datePickerSx={{ paddingTop: '5px !important', paddingBottom: '5px !important' }}
            value={[formValues.startBetweenDate, formValues.endBetweenDate]}
            setValue={(start, end) =>
              dispatch(updateFormValues({ ...formValues, startBetweenDate: start, endBetweenDate: end }))
            }
          />
        </>
      )}
      {takeOption && !skipOption && (
        <Box>
          <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>Check this amount of invoices:</DialogContentText>
          <TextField
            {...numberInputDefaults}
            fullWidth
            value={formValues.take}
            onChange={(e) => dispatch(updateFormValues({ ...formValues, take: e.target.value }))}
            autoComplete="off"
          />
        </Box>
      )}
      {takeOption && skipOption && (
        <SpacedRow>
          <Box>
            <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>Check this amount of invoices:</DialogContentText>
            <TextField
              {...numberInputDefaults}
              fullWidth
              value={formValues.take}
              onChange={(e) => dispatch(updateFormValues({ ...formValues, take: e.target.value }))}
              autoComplete="off"
            />
          </Box>
          <Box>
            <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>Skip this amount of invoices:</DialogContentText>
            <TextField
              {...numberInputDefaults}
              fullWidth
              value={formValues.skip}
              onChange={(e) => dispatch(updateFormValues({ ...formValues, skip: e.target.value }))}
              autoComplete="off"
            />
          </Box>
        </SpacedRow>
      )}

      {companiesOption && facilitiesAndCompanies.companies.length > 0 && (
        <>
          <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>
            {companiesOption.multiselect ? 'Groups:' : 'Group:'}
          </DialogContentText>
          <Autocomplete
            {...autoCompleteDefaults(
              companiesOption,
              facilitiesAndCompanies.companies,
              formValues.companies,
              showAllGroups,
              setShowAllGroups,
              (newValues) => {
                setFilteredReportFacilities([
                  ...facilitiesAndCompanies.facilities.filter(
                    (facility) => !newValues.length || newValues.some((v) => v.value === facility.companyId)
                  ),
                ]);
                dispatch(
                  updateFormValues({
                    ...formValues,
                    companies: newValues,
                  })
                );
              }
            )}
          />
        </>
      )}
      {facilityOption && (
        <>
          <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>
            {facilityOption.multiselect ? 'Facilities:' : 'Facility:'}{' '}
          </DialogContentText>
          <Autocomplete
            {...autoCompleteDefaults(
              facilityOption,
              filteredReportFacilities,
              formValues.facilities,
              showAllFacilities,
              setShowAllFacilities,
              (newValues) => {
                dispatch(
                  updateFormValues({
                    ...formValues,
                    facilities: newValues,
                  })
                );
              }
            )}
          />
        </>
      )}
      {vendorOption && (
        <>
          <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>
            {vendorOption.multiselect ? 'Vendors:' : 'Vendor'}
          </DialogContentText>
          <Autocomplete
            {...autoCompleteDefaults(
              vendorOption,
              createReportData.vendors,
              formValues.vendors,
              showAllVendors,
              setShowAllVendors,
              (newValues) => dispatch(updateFormValues({ ...formValues, vendors: newValues }))
            )}
          />
        </>
      )}
      {priceChangesOnly && (
        <FormControlLabel
          sx={{ mt: 1 }}
          control={
            <Checkbox
              checked={formValues.priceChangesOnly !== undefined ? formValues.priceChangesOnly : false}
              onClick={(event) => event.stopPropagation()}
              onChange={(e) => handleCheckboxChange('priceChangesOnly', e.target.checked)}
            />
          }
          label="Show Only Items with Price Change"
        />
      )}
      {rentalVendor && (
        <>
          <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>Rental Vendor</DialogContentText>
          <Autocomplete
            {...autoCompleteDefaults(
              rentalVendor,
              createReportData.rentalVendors,
              [formValues.rentalVendor],
              showAllRentalVendors,
              setShowAllRentalVendors,
              (newValues) => dispatch(updateFormValues({ ...formValues, rentalVendor: newValues?.[0] }))
            )}
          />
        </>
      )}
      {categoriesOption && (
        <>
          <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>
            {categoriesOption.multiselect ? 'Categories:' : 'Category:'}
          </DialogContentText>
          <Autocomplete
            {...autoCompleteDefaults(
              categoriesOption,
              createReportData.categories,
              formValues.categories,
              showAllCategories,
              setShowAllCategories,
              (newValues) => {
                setFilteredReportSubcats(
                  createReportData.subcategories.filter((s) => newValues.some((v) => v.id === s.mainCategoryId))
                );
                dispatch(
                  updateFormValues({
                    ...formValues,
                    categories: newValues,
                  })
                );
              }
            )}
          />
        </>
      )}
      {subcategoriesOption && (
        <>
          <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>
            {subcategoriesOption.multiselect ? 'Subcategories:' : 'Subcategory:'}
          </DialogContentText>
          <Autocomplete
            {...autoCompleteDefaults(
              subcategoriesOption,
              filteredReportSubcats,
              formValues.subcategories,
              showAllSubCategories,
              setShowAllSubCategories,
              (newValues) =>
                dispatch(
                  updateFormValues({
                    ...formValues,
                    subcategories: newValues,
                  })
                )
            )}
          />
        </>
      )}
      {timeFrameFilterOptions && (
        <>
          <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>
            {timeFrameFilterOptions.multiselect ? 'Time Frames:' : 'Time Frame:'}
          </DialogContentText>
          <Autocomplete
            {...autoCompleteDefaults(
              timeFrameFilterOptions,
              timeframeFilters,
              formValues.timeFrameFilters,
              showAllTimeFrameFilters,
              setShowAllTimeFrameFilters,
              (newValues) =>
                dispatch(
                  updateFormValues({
                    ...formValues,
                    timeFrameFilters: newValues,
                  })
                )
            )}
          />
        </>
      )}
      {serialNumbers && (
        <>
          <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>Serial Numbers:</DialogContentText>
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={formValues.serialNumbers || []}
            inputValue={inputValue}
            onInputChange={(event, newValue, reason) => {
              if (reason === 'input') {
                setInputValue(newValue);
              }
            }}
            onChange={(event, newValue, reason) => {
              if (reason === 'removeOption' || reason === 'clear') {
                dispatch(
                  updateFormValues({
                    ...formValues,
                    serialNumbers: newValue,
                  })
                );
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Enter serials..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSave();
                  }
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const pastedText = e.clipboardData.getData('text');
                  handleSave(pastedText);
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {inputValue && (
                        <IconButton size="small" onClick={() => handleSave()}>
                          <SaveIcon fontSize="small" />
                        </IconButton>
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </>
      )}
      {transportStatuses && (
        <>
          <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>Transport Statuses</DialogContentText>
          <Autocomplete
            {...autoCompleteDefaults(
              transportStatuses,
              createReportData.transportStatuses,
              formValues.transportStatuses,
              showAllTransportStatuses,
              setShowAllTransportStatuses,
              (newValues) => dispatch(updateFormValues({ ...formValues, transportStatuses: newValues }))
            )}
          />
        </>
      )}
      <Grid item sx={{ mt: 2 }}>
        {pendingOnly && (
          <FormControlLabel
            control={
              <Checkbox
                checked={formValues.pendingOnly !== undefined ? formValues.pendingOnly : false}
                onClick={(event) => event.stopPropagation()}
                onChange={(e) => handleCheckboxChange('pendingOnly', e.target.checked)}
              />
            }
            label="Only Include Pending Invoices"
          />
        )}
      </Grid>
      <Grid item sx={{ mt: 2 }}>
        {useBudgetDate && (
          <FormControl fullWidth>
            <DialogContentText id="report-date-basis-label" sx={{ fontWeight: 'bold', mb: 1 }}>Filter report by:</DialogContentText>
            <RadioGroup
              aria-labelledby="report-date-basis-label"
              value={formValues.useBudgetDate ? 'budget' : 'order'}
              onClick={(event) => event.stopPropagation()}
              onChange={(_, value) => handleCheckboxChange('useBudgetDate', value === 'budget')}
              sx={{ flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}
            >
              {[
                { value: 'order', label: 'Order Approved Date', description: 'Uses the date the order was approved.' },
                { value: 'budget', label: 'Budget Date', description: 'Uses the budget date, or order approval date if no budget date is set.' },
              ].map((option) => {
                const selected = (formValues.useBudgetDate ? 'budget' : 'order') === option.value;
                return (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio size="small" sx={{ p: 0, mr: 1, mt: 0.25 }} />}
                    label={
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontSize: '0.9375rem' }}>{option.label}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{option.description}</Typography>
                      </Box>
                    }
                    sx={{
                      m: 0,
                      p: 1,
                      flex: 1,
                      alignItems: 'flex-start',
                      border: 2,
                      borderColor: selected ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      bgcolor: selected ? 'action.selected' : 'background.paper',
                      '&:hover': { bgcolor: 'action.hover' },
                      '&:focus-within': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
                    }}
                  />
                );
              })}
            </RadioGroup>
          </FormControl>
        )}
      </Grid>
      {formValues.selectedReport.id === 15 && (excTicketData || incChildFacs || incFacsWOGrps) && (
        <>
          <DialogTitle sx={{ textAlign: 'left', color: 'primary.main', p: '8px' }}>
            {user.sites.find((site) => site.value === user.siteId)?.label} Operations Report
          </DialogTitle>
          <DialogTitle sx={{ textAlign: 'left', color: 'primary.main', p: '8px' }}>Filters:</DialogTitle>
        </>
      )}
      <Grid item sx={{ ml: 1 }}>
        {excTicketData && (
          <Tooltip title="Exclude analytics from the site's ticket section" placement="right">
            <FormControlLabel
              control={
                <Checkbox
                  checked={formValues.excludeTicketData !== undefined ? formValues.excludeTicketData : false}
                  onClick={(event) => event.stopPropagation()}
                  onChange={(e) => handleCheckboxChange('excludeTicketData', e.target.checked)}
                />
              }
              label="Exclude Ticket Data"
            />
          </Tooltip>
        )}
      </Grid>
      <Grid item sx={{ ml: 1 }}>
        {incChildFacs && (
          <Tooltip title="Add data from facilities grouped under each parent facility" placement="right">
            <FormControlLabel
              control={
                <Checkbox
                  checked={formValues.includeChildFacs !== undefined ? formValues.includeChildFacs : false}
                  onClick={(event) => event.stopPropagation()}
                  onChange={(e) => handleCheckboxChange('includeChildFacs', e.target.checked)}
                />
              }
              label="Include Child Facilities"
            />
          </Tooltip>
        )}
      </Grid>
      <Grid item sx={{ ml: 1 }}>
        {incFacsWOGrps && (
          <Tooltip title="Includes facilities that are not assigned to any group" placement="right">
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    formValues.includeFacsWithoutGroup !== undefined ? formValues.includeFacsWithoutGroup : false
                  }
                  onClick={(event) => event.stopPropagation()}
                  onChange={(e) => handleCheckboxChange('includeFacsWithoutGroup', e.target.checked)}
                />
              }
              label="Include Facilities that are not Associated with a Group"
            />
          </Tooltip>
        )}
      </Grid>
      {formValues.selectedReport.id === 20 && (
        <Grid item sx={{ ml: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formValues.includePDFs ?? false}
                onClick={(event) => event.stopPropagation()}
                onChange={(e) => handleCheckboxChange('includePDFs', e.target.checked)}
              />
            }
            label="Include PDFs"
          />
        </Grid>
      )}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        {!(isScheduleReport && selectedReportId === 12) && (monthOptions || yearOptions) && (
          <>
            <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>
              {monthOptions?.multiselect || yearOptions?.multiselect ? 'Months and Years:' : 'Month and Year:'}
            </DialogContentText>

            <DatePicker
              views={['year', 'month']}
              value={
                formValues.year?.[0] !== undefined && formValues.month?.[0] !== undefined
                  ? new Date(formValues.year[0], formValues.month[0] - 1)
                  : null
              }
              onChange={(newValue) => {
                if (newValue instanceof Date && !newValue.isNaN) {
                  const month = newValue.getMonth() + 1;
                  const year = newValue.getFullYear();

                  dispatch(
                    updateFormValues({
                      ...formValues,
                      month: [{ value: month }],
                      year: [{ value: year }],
                    })
                  );
                }
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />
          </>
        )}
      </LocalizationProvider>
    </>
  );
}
